'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import Lenis from 'lenis'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

interface SmoothScrollProps {
  children: React.ReactNode
}

export default function SmoothScroll({ children }: SmoothScrollProps) {
  const lenisRef = useRef<Lenis | null>(null)
  const pathname = usePathname()

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      touchMultiplier: 2,
      infinite: false,
    })

    lenisRef.current = lenis

    // Sync Lenis scroll position with GSAP ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update)

    // Use GSAP ticker as the animation loop for Lenis
    const tickerCallback = (time: number) => {
      lenis.raf(time * 1000) // GSAP ticker time is in seconds, Lenis expects ms
    }
    gsap.ticker.add(tickerCallback)

    // Disable Lenis' own rAF since GSAP ticker drives it
    gsap.ticker.lagSmoothing(0)

    return () => {
      gsap.ticker.remove(tickerCallback)
      lenis.destroy()
      lenisRef.current = null
    }
  }, [])

  // Reset scroll to top on route changes. Next.js App Router normally
  // handles this, but Lenis owns the scroll state and bypasses the
  // browser default — so we manually jump to 0 on each pathname change.
  useEffect(() => {
    const lenis = lenisRef.current
    if (lenis) {
      lenis.scrollTo(0, { immediate: true, force: true })
    } else {
      window.scrollTo(0, 0)
    }
    // Re-sync any scroll-bound animations to the new position.
    ScrollTrigger.refresh()
  }, [pathname])

  return <>{children}</>
}
